import React, { useMemo } from 'react';
import { Polyline } from 'react-leaflet';
import type { Position } from '../../types.js';

interface SurfaceHighlightLayerProps {
  route: Position[];
  highlightedSurface: string | null;
  highlightedSegmentIndex?: number; // Index of the specific segment to highlight
  routeSegments: any[];
}

/**
 * Layer that highlights specific surface types on the route
 * by creating colored overlays over the matching segments
 */
export const SurfaceHighlightLayer: React.FC<SurfaceHighlightLayerProps> = ({
  route,
  highlightedSurface,
  highlightedSegmentIndex,
  routeSegments
}) => {
  // Generate highlighted segments
  const highlightedSegments = useMemo(() => {
    if (!highlightedSurface || !routeSegments || !route || route.length === 0) {
      return [];
    }

    // Calculate cumulative distances for route coordinates
    const routeDistances = [0]; // Start with 0 distance
    for (let i = 1; i < route.length; i++) {
      const prevPoint = route[i - 1];
      const currentPoint = route[i];
      
      // Calculate distance between consecutive points using Haversine formula
      const R = 6371000; // Earth's radius in meters
      const φ1 = (prevPoint.lat * Math.PI) / 180;
      const φ2 = (currentPoint.lat * Math.PI) / 180;
      const Δφ = ((currentPoint.lat - prevPoint.lat) * Math.PI) / 180;
      const Δλ = ((currentPoint.lng - prevPoint.lng) * Math.PI) / 180;

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
               Math.cos(φ1) * Math.cos(φ2) *
               Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const segmentDistance = R * c;
      
      routeDistances.push(routeDistances[i - 1] + segmentDistance);
    }

    // Find segments that match the highlighted surface
    const matchingSurfaces = routeSegments.filter(seg => seg.surface === highlightedSurface);
    
    // Always highlight ALL segments of the selected surface type
    // The segment index is only used for map centering, not for limiting highlighting
    const segmentsToHighlight = matchingSurfaces;

    // Process route segments and find matching surfaces
    const segments = [];
    let cumulativeSegmentDistance = 0;

    for (let i = 0; i < routeSegments.length; i++) {
      const segment = routeSegments[i];
      const segmentDistance = segment.Distance ? Number(segment.Distance) : 0;
      
      // Check if this segment is in our list of segments to highlight
      if (segmentsToHighlight.includes(segment) && segmentDistance > 0) {
        const segmentStartDistance = cumulativeSegmentDistance;
        const segmentEndDistance = cumulativeSegmentDistance + segmentDistance;
        
        // Find corresponding route coordinate indices based on distance
        let startIndex = 0;
        let endIndex = route.length - 1;
        
        // Find start index
        for (let j = 0; j < routeDistances.length; j++) {
          if (routeDistances[j] >= segmentStartDistance) {
            startIndex = Math.max(0, j - 1); // Take previous point to ensure coverage
            break;
          }
        }
        
        // Find end index
        for (let j = startIndex; j < routeDistances.length; j++) {
          if (routeDistances[j] >= segmentEndDistance) {
            endIndex = Math.min(route.length - 1, j + 1); // Take next point to ensure coverage
            break;
          }
        }
        
        // Extract coordinates for this segment
        if (startIndex < endIndex) {
          const segmentCoordinates = route.slice(startIndex, endIndex + 1);
          if (segmentCoordinates.length > 1) {
            const isCurrentlyFocused = highlightedSegmentIndex === -1 ? false : segmentsToHighlight.indexOf(segment) === highlightedSegmentIndex;
            
            segments.push({
              coordinates: segmentCoordinates,
              surface: segment.surface,
              combinedSegmentCount: segment.combinedSegmentCount || 1,
              startDistance: segmentStartDistance,
              endDistance: segmentEndDistance,
              isCurrentlyFocused // Mark if this is the focused segment, unless all segments are highlighted (-1)
            });
          }
        }
      }
      
      cumulativeSegmentDistance += segmentDistance;
    }

    return segments;
  }, [route, highlightedSurface, highlightedSegmentIndex, routeSegments]);

  if (!highlightedSurface || highlightedSegments.length === 0) {
    return null;
  }

  return (
    <>
      {highlightedSegments.map((segment, index) => (
        <Polyline
          key={`highlight-${index}`}
          positions={segment.coordinates}
          pathOptions={{
            color: highlightedSegmentIndex === -1 ? '#ff0000' : (segment.isCurrentlyFocused ? '#ff6600' : '#ff0000'), // All red when showing all segments (-1), otherwise orange for focused, red for others
            weight: highlightedSegmentIndex === -1 ? 10 : (segment.isCurrentlyFocused ? 12 : 8), // Consistent weight when showing all segments, otherwise thicker for focused segment
            opacity: highlightedSegmentIndex === -1 ? 0.7 : (segment.isCurrentlyFocused ? 0.9 : 0.6), // Consistent opacity when showing all segments, otherwise more opaque for focused
            lineCap: 'round',
            lineJoin: 'round',
            dashArray: highlightedSegmentIndex === -1 ? '6, 3' : (segment.isCurrentlyFocused ? '8, 4' : '10, 5') // Consistent dash pattern when showing all segments
          }}
          interactive={false}
        />
      ))}
      
      {/* Add an additional pulsing overlay for the focused segment (only when not showing all segments) */}
      {highlightedSegmentIndex !== -1 && highlightedSegments.filter(seg => seg.isCurrentlyFocused).map((segment, index) => (
        <Polyline
          key={`pulse-${index}`}
          positions={segment.coordinates}
          pathOptions={{
            color: '#ffaa00',
            weight: 16,
            opacity: 0.3,
            lineCap: 'round',
            lineJoin: 'round',
            dashArray: '4, 2'
          }}
          interactive={false}
        />
      ))}
    </>
  );
};
